import {connect} from "react-redux";
import {selectProject} from "../../redux/selectors/editor";
import {Box, Paper, Tab} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {SyntheticEvent, useContext, useEffect, useState} from "react";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {selectAssets, selectProjects} from "../../redux/selectors/firebase";
import {ProjectDetails} from "../../redux/types/firebase";
import {VideoAssets} from "./VideoAssets";
import {FrameIOContent, IFrameIORead} from "./content/FrameIOContent";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {AWSContent, IS3ReadWrite} from "./content/AWSContent";
import {AssetsConfig} from "./config/AssetsConfig";
import {SoundAssets} from "./SoundAssets";
import {IContentData} from "./content/FileSystemContent";

const useScriptStyles = makeStyles(theme => ({
    paper: {
        display: 'flex',
        padding: theme.spacing(1),
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
    tabs: {
        display: 'flex',
        flexDirection: "column",
        width: "100%",
    },
    tabPanels: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "calc(100% - 48px)"
    },
    tabPanel: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "calc(100% - 48px)"
    }
}));

interface IAssetData {
    aws: IContentData
    frame: IContentData
}
interface IProps {
    assets: IAssetData
    project: string
    projects: {[project: string]: ProjectDetails}
}
function Assets({assets, project, projects}: IProps) {
    const classes = useScriptStyles();
    const {get, track, untrack} = useContext(FirebaseContext);
    const [awsContent, setAWSContent] = useState<AWSContent>();
    const [frameIOContent, setFrameIOContent] = useState<FrameIOContent>();
    const [tab, setTab] = useState("config");
    const handleTabChange = (event: SyntheticEvent, value: any) => setTab(value);

    useEffect(() => {
        track(`assets/${project}`, ["assets"]);
        track(`configs/${project}`, ["config"]);
        return () => {
            untrack(`assets/${project}`);
            untrack(`configs/${project}`);
        }
    }, [project, track, untrack]);
    useEffect(() => {
        if (assets?.aws) {
            get("secrets/s3-read-write").then((result) => {
                const content = new AWSContent(result as IS3ReadWrite, projects[project].name);

                content.read(assets.aws);
                setAWSContent(content);
            });
        }
        if (assets?.frame) {
            get("secrets/frame-io-read").then((result: any) => {
                const content = new FrameIOContent(result as IFrameIORead, projects[project].name);

                content.read(assets.frame);
                setFrameIOContent(content);
            });
        }
    }, [assets, get, project, projects]);
    return (
        <Paper className={classes.paper}>
            <TabContext value={tab}>
                <Box className={classes.tabs}>
                    <TabList onChange={handleTabChange}>
                        <Tab label="Config" value="config"/>
                        <Tab disabled={! (awsContent && frameIOContent)} label="Video" value="video"/>
                        <Tab disabled={! (awsContent && frameIOContent)} label="Sound" value="sound"/>
                    </TabList>
                    {tab === "config" &&
                        <Box className={classes.tabPanels}>
                            <TabPanel className={classes.tabPanel} value="config">
                                <AssetsConfig project={project} projects={projects}/>
                            </TabPanel>
                        </Box>}
                    {tab === "video" && awsContent && frameIOContent &&
                        <Box className={classes.tabPanels}>
                            <TabPanel className={classes.tabPanel} value="video">
                                <VideoAssets awsContent={awsContent} frameIOContent={frameIOContent}/>
                            </TabPanel>
                        </Box>}
                    {tab === "sound" && awsContent && frameIOContent &&
                        <Box className={classes.tabPanels}>
                            <TabPanel className={classes.tabPanel} value="sound">
                                <SoundAssets awsContent={awsContent} frameIOContent={frameIOContent}/>
                            </TabPanel>
                        </Box>}
                </Box>
            </TabContext>
        </Paper>
    );
}
const mapStateToProps = (store: never) => ({
    assets: selectAssets(store),
    project: selectProject(store),
    projects: selectProjects(store),
});

export default connect(mapStateToProps, {
})(Assets);
